import { useState, useEffect } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import jwt_decode from 'jwt-decode';
import { useCookies } from "react-cookie";
import { ToastContainer, cssTransition } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import useLatestVersionAlert from './components/Common/SharedComponents/useLatestVersionAlert';
import AppContext, { AppContextType, AuthTokens, token_config } from './AppContext';
import LoginPage from './components/pages/LoginPage';
import DashboardPage from './components/pages/DashboardPage';
import CaseForm from './components/pages/CaseForm';
import CaseWebForm from './components/pages/CaseWebForm';
import Constituents from './components/pages/Constituents';
import ConstituentForm from './components/pages/ConstituentForm';
import Reports from './components/pages/Reports';
import Users from './components/pages/Users';
import UserForm from './components/pages/UserForm';
import Offices from './components/pages/Offices';
import OfficeForm from './components/pages/OfficeForm';
import NotificationForm from './components/pages/NotificationForm';
import Tags from './components/pages/Tags';
import TagForm from './components/pages/TagForm';
import AddressBook from './components/pages/AddressBook';
import AddressBookForm from './components/pages/AddressBookForm';
import Neighborhoods from './components/pages/Neighborhoods';
import NeighborhoodForm from './components/pages/NeighborhoodForm';
import CreatePasswordForm from './components/pages/CreatePasswordForm';
import ResetPasswordForm from './components/pages/ResetPasswordForm';
import ForgotPasswordForm from './components/pages/ForgotPasswordForm';
import ChangePasswordForm from './components/pages/ChangePasswordForm';
import ContactForm from './components/pages/ContactForm';
import ReplyMessage from './components/pages/ReplyMessage';
import EmailSignature from './components/pages/EmailSignature';
import MSGraphSubscription from './components/pages/MSGraphSubscription';
import { ProtectedRoute } from './helpers/ProtectedRoute';
import NotFoundPage from './helpers/NotFoundPage';
import { DecodedJWT } from './types/ServerDTO';
import NavMobile from "./blocks/nav-mobile";
import './App.css';

function App() {
  const location = useLocation();
  const currentPath = location.pathname.split('/')[1];
  const currentPathClass = currentPath !== '' ? currentPath : 'login';
  const [cookies, setCookie] = useCookies(["theme", "isThemSet"]);
  const [themeVersion, setThemeVersion] = useState(cookies.theme || "white");
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [authTokens, setAuthTokens] = useState<AuthTokens | null>(null);
  const [userInfo, setUserInfo] = useState<DecodedJWT>();
  const { checkVersionUpdate } = useLatestVersionAlert();
  const [notificationCount, setNotificationCount] = useState<number>(0);

  console.log(`process Environment => ${process.env.NODE_ENV}`);
  if (process.env.NODE_ENV !== "development") {
    // console.log = function () { };
  }

  useEffect(() => {
    console.log("Checking update...");
    checkVersionUpdate();
  }, []);

  useEffect(() => {
    if (
      window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: dark)").matches &&
      cookies.isThemSet !== "true"
    ) {
      setThemeVersion("black");
    }

    // Retrieve the access token from localStorage on page load
    const storedAccessToken = localStorage.getItem(token_config.ACCESS_TOKEN);
    const storedRefreshToken = localStorage.getItem(token_config.REFRESH_TOKEN);
    if (storedAccessToken) {
      setAuthTokens({ accessToken: storedAccessToken, refreshToken: storedRefreshToken });
      setIsLoggedIn(true);
    } else {
      setIsLoggedIn(false);
    }
  }, []);

  useEffect(() => {
    // Store the access token in localStorage
    if (authTokens) {
      localStorage.setItem("accessToken", authTokens?.accessToken || "");
      localStorage.setItem("refreshToken", authTokens?.refreshToken || "");
      if (authTokens && authTokens.accessToken) {
        const decoded: DecodedJWT = jwt_decode(authTokens.accessToken);
        setUserInfo(decoded);
      }
    }
  }, [authTokens]);

  const handleThemeChange = () => {
    const theme = themeVersion === "black" ? "white" : "black";

    setThemeVersion(theme);

    setCookie("theme", theme, { path: "/" });
    setCookie("isThemSet", "true", { path: "/" });
  };

  const updateNotificationCount = (tmpNotificationCount: number) => {
    setNotificationCount(tmpNotificationCount);
  };

  const handleShowNav = () => {
    setIsNavOpen(!isNavOpen);
  };

  const mainClass = (themeVersion === "black" && location.pathname.indexOf('/forms/') === -1) ? "main dark-theme" : "main";
  const appContextValues: AppContextType = {
    authTokens,
    setAuthTokens,
    isNavOpen,
    isLoggedIn,
    themeVersion,
    handleThemeChange,
    handleShowNav,
    userInfo,
    updateNotificationCount,
    notificationCount,
  };

  const DisableTransition = cssTransition({
    enter: "disable-transition",
    exit: "disable-transition",
    collapse: false
  });

  return (
    <div className={`App ${currentPathClass}`}>
      <div className={mainClass}>
        <ToastContainer hideProgressBar autoClose={500} transition={DisableTransition} />
        <AppContext.Provider value={appContextValues}>
          <NavMobile isOpen={isNavOpen} />
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forms/:officeGuid" element={<CaseWebForm />} />
            <Route path="/create_password/:userGuid" element={<CreatePasswordForm />} />
            <Route path="/forgot_password" element={<ForgotPasswordForm />} />
            <Route path="/reset_password/:userGuid" element={<ResetPasswordForm />} />
            <Route element={<ProtectedRoute />}>
              <Route path="/" element={<Navigate to="/dashboard" />} />
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/case" element={<CaseForm />} />
              <Route path="/case/:id" element={<CaseForm />} />
              <Route path="/constituents" element={<Constituents />} />
              <Route path="/constituent" element={<ConstituentForm />} />
              <Route path="/constituent/:id" element={<ConstituentForm />} />
              <Route path="/reports" element={<Reports />} />
              <Route path="/users" element={<Users />} />
              <Route path="/user" element={<UserForm />} />
              <Route path="/user/:id" element={<UserForm />} />
              <Route path="/offices" element={<Offices />} />
              <Route path="/office" element={<OfficeForm />} />
              <Route path="/office/:id" element={<OfficeForm />} />
              <Route path="/address_books" element={<AddressBook />} />
              <Route path="/address_book" element={<AddressBookForm />} />
              <Route path="/address_book/:id" element={<AddressBookForm />} />
              <Route path="/notifications" element={<NotificationForm />} />
              <Route path="/tags" element={<Tags />} />
              <Route path="/tag" element={<TagForm />} />
              <Route path="/tag/:id" element={<TagForm />} />
              <Route path="/neighborhoods" element={<Neighborhoods />} />
              <Route path="/neighborhood" element={<NeighborhoodForm />} />
              <Route path="/neighborhood/:id" element={<NeighborhoodForm />} />
              <Route path="/contact-form" element={<ContactForm />} />
              <Route path="/reply-message" element={<ReplyMessage />} />
              <Route path="/email-signature" element={<EmailSignature />} />
              <Route path="/change-password" element={<ChangePasswordForm />} />
              <Route path="/ms-graph-subscription" element={<MSGraphSubscription />} />
            </Route>
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </AppContext.Provider>
      </div>
    </div>
  );
}

export default App;
